import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../../../environments/environment.interface';
import { Assignment } from '../../../../../api/app-types/assignment';
import { CreateAdditionalAssignmentBody } from '../../../../../api/app-types/additional-assignment';
import { Client } from '../../../../../api/app-types/client.app';
import { DisabilityKind } from '../../../../../api/app-types/disability-type.app';
import { DocumentFile, DocumentFileSubjectType } from '../../../../../api/app-types/document.app';
import { TypeOfCare } from '../../../../../api/app-types/type-of-care';
import { User } from '../../../../../api/app-types/user.app';
import { PERSON_TYPE, SearchResultItem } from '../../../../../api/app-types/search.app';
import { CATEGORIES_API } from '../../../../../api/injection-tokens/services.token';
import { ADDITIONAL_ASSIGNMENT_API } from '../../../../../api/services/additional-assignment.service';
import { USER_SERVICE } from '../../../../../api/services/user.service';
import { CUSTOM_DATE_FORMATS } from '../../../../../core/adapters/custom-date-format';
import { LvrField } from '../../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../../core/directives/lvr-input.directive';
import { Participants } from '../../../../contact/participants/participants';
import { DocumentUploadControl } from '../../../../../shared/document-upload/document-upload';
import {
	allowedFileTypes,
	maxFiles,
	maxFileSize,
} from '../../../../../shared/document-upload/document-upload-validators';
import { formatDateToISOString, matDateProviders } from '../../../../../shared/helpers/date.func';
import {
	resolveMaxStartDate,
	resolveMinStartDate,
	resolveP3StartDate,
} from '../../../../../shared/helpers/additional-assignment-date.helper';
import { hasError } from '../../../../../shared/helpers/has-error.func';
import { LvrPaddingDirective } from '../../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../../../shared/pipes/append-asterisk.pipe';
import { LvrRestoreActions } from '../../../../../shared/storage/restore-actions';
import { ClientResolved } from '../../../../../shared/routing/resolvers/client.resolver';
import { ImmediateErrorStateMatcher } from '../../../../../shared/validators/immediate-error-state-matcher';
import { maxDateValidator, minDateValidator } from '../../../../../shared/validators/max-date-validator';
import { FORM_KEYS } from '../../../../../shared/storage/form-keys.type';
import { LvrDefaultActions } from '../../../../../core/components/default-actions/default.actions';
import { hasPermission } from '../../../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../../../shared/storage/session-manager.service';
import { STATE } from '../../../../../core/services/state.service';
import { FormPersistenceService } from '../../../../../shared/storage/form-persistence.service';
import { LvrAutoSaveDirective } from '../../../../../shared/storage/auto-save.directive';
import { NOTIFICATION_SERVICE } from '../../../../../shared/notification/notification';
import {
	createErrorNotification,
	createSuccessNotification,
} from '../../../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-create-additional-assignment',
	imports: [
		TranslocoPipe,
		DatePipe,
		LvrPaddingDirective,
		ReactiveFormsModule,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		MatDatepickerModule,
		MatNativeDateModule,
		MatInput,
		MatSelectModule,
		LvrAppendAsteriskPipe,
		DocumentUploadControl,
		LvrRestoreActions,
		Participants,
		LvrDefaultActions,
		LvrAutoSaveDirective,
	],
	templateUrl: './create-additional-assignment.html',
	styleUrl: './create-additional-assignment.scss',
	providers: [matDateProviders],
})
export class CreateAdditionalAssignment {
	private readonly env = inject(ENVIRONMENT);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly fb = inject(FormBuilder);
	private readonly categoriesApi = inject(CATEGORIES_API);
	private readonly additionalAssignmentApi = inject(ADDITIONAL_ASSIGNMENT_API);
	private readonly userService = inject(USER_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly draftService = inject(FormPersistenceService);
	private readonly transloco = inject(TranslocoService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly state = inject(STATE);
	private readonly data = toSignal(this.route.data, {
		initialValue: this.route.snapshot.data as Record<string, unknown>,
	});
	private readonly assignment = computed(() => this.data()['assignment'] as Assignment | undefined);
	private readonly p3StartDate = computed(() => resolveP3StartDate(this.assignment()));
	private readonly clientResolved = computed(() => this.data()['client'] as ClientResolved | undefined);
	public readonly client = computed<Client | undefined>(() => this.clientResolved()?.value as Client);
	public readonly clientFullName = computed(() => {
		const client = this.client();
		if (!client) return '';
		return client.firstName ? `${client.firstName} ${client.name}` : client.name;
	});
	public readonly draftId = FORM_KEYS.CREATE_ADDITIONAL;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateDisplayFormat: string = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly minStartDate = computed(() => resolveMinStartDate(this.env, this.p3StartDate()));
	public readonly maxStartDate = computed(() => resolveMaxStartDate());
	public readonly maxFileCount = this.env.contactSettings.maxFiles;
	public readonly maxFileSize = this.env.fileSettings.maxFileSize;
	public readonly subjectType = DocumentFileSubjectType.ASSIGNMENT;
	public readonly canCreateAssignmentsPermission = hasPermission(this.session.permissions, 'Assignments.Create');
	public readonly canManageAssignmentDocuments = hasPermission(this.session.permissions, 'Documents.Assignment.Create');
	public readonly isLoading = signal(false);

	public readonly disabilityKindsPage = computed(
		() => this.data()['disabilityKinds'] as PaginatedResponse<DisabilityKind>,
	);
	public readonly disabilityKinds = computed(() => this.disabilityKindsPage()?.entities ?? []);
	public readonly usersPage = signal<PaginatedResponse<User> | undefined>(undefined);
	public readonly users = computed(() => this.usersPage()?.entities ?? []);
	public readonly typesOfCare = signal<TypeOfCare[]>([]);
	public readonly specialistSearchItems = computed(() => this.users().map((user) => this.toSearchResultItem(user)));

	public readonly form = this.fb.group({
		startOfCare: this.fb.control<Date | null>(null, [
			minDateValidator(this.minStartDate()),
			maxDateValidator(this.maxStartDate()),
		]),
		categoryTypeOfCareId: this.fb.control<number | null>(null, [Validators.required]),
		participants: this.fb.control<SearchResultItem[]>([], [CreateAdditionalAssignment.participantsRequiredValidator]),
		careRelatedHandycapId: this.fb.control<number | null>(null),
		documents: this.fb.control<DocumentFile[]>(
			[],
			[
				maxFiles(this.maxFileCount),
				maxFileSize(this.maxFileSize),
				allowedFileTypes(this.env.fileSettings.allowedMimeTypes),
			],
		),
	});
	private readonly categoryTypeOfCareId = toSignal(this.form.get('categoryTypeOfCareId')!.valueChanges, {
		initialValue: this.form.get('categoryTypeOfCareId')!.value,
	});
	private readonly lastTypeOfCareId = signal<number | null>(this.form.get('categoryTypeOfCareId')!.value ?? null);
	public readonly userSpecializationsByObjectId = computed(() => {
		const map: Record<string, string> = {};
		for (const user of this.users()) {
			if (!user.objectId) continue;
			const specialization = user.specialization?.shortName;
			if (!specialization) continue;
			map[user.objectId] = specialization;
		}
		return map;
	});

	public constructor() {
		void this.loadTypesOfCare();

		effect(() => {
			const typeId = this.categoryTypeOfCareId();
			const participantsCtrl = this.form.get('participants');
			const lastTypeId = this.lastTypeOfCareId();
			if (typeId !== lastTypeId) {
				participantsCtrl?.setValue([]);
				this.lastTypeOfCareId.set(typeId ?? null);
			}
			if (!typeId) {
				this.usersPage.set({
					entities: [],
					offset: this.env.pagination.offset,
					range: this.env.pagination.range,
					totalEntities: 0,
				});
				participantsCtrl?.setValue([]);
				participantsCtrl?.disable({ emitEvent: false });
				return;
			}
			void this.loadSpecialistUsers(typeId);
			participantsCtrl?.disable({ emitEvent: false });
		});

		effect(() => {
			const min = this.minStartDate();
			const max = this.maxStartDate();
			const control = this.form.get('startOfCare');
			if (!control) return;
			control.setValidators([minDateValidator(min), maxDateValidator(max)]);
			control.updateValueAndValidity({ emitEvent: false });
		});
	}

	public get documentsIsRequired() {
		const docs = this.form.get('documents')!;
		return docs.validator && docs.hasValidator(Validators.required);
	}

	public getDisabilityKindName(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.disabilityType?.remark ?? '';
	}

	public getDisabilityKindDifferentiation(id: number) {
		if (!id) return '';
		return this.disabilityKinds().find((b) => b.id === id)?.categoryDiagnosis?.description ?? '-';
	}

	public hasError(control: AbstractControl | null, ...errors: string[]) {
		return hasError(control, ...errors);
	}

	/**
	 * Checks if documents form control has a specific validation error and has been touched.
	 * @param control The form control to check.
	 * @param errors The name errors to look for.
	 * @returns The error key or empty string.
	 */
	public hasFileError(control: AbstractControl | null, ...errors: string[]) {
		if (!control) return '';

		const error = errors.find(
			(error) => control.hasError(error) && (control.dirty || !!control.value?.length || control.touched),
		);

		if (!error) return '';
		return error;
	}

	private static participantsRequiredValidator(control: AbstractControl): ValidationErrors | null {
		const value = control.value as SearchResultItem[] | null | undefined;
		if (!value || !Array.isArray(value) || value.length === 0) {
			return { required: true };
		}
		return null;
	}

	public async save() {
		if (this.isLoading()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const clientId = this.client()?.id;
		if (!clientId) return;

		const assignmentId = this.assignment()?.id;
		if (!assignmentId) return;

		const formValue = this.form.value;
		const participants = (formValue.participants ?? []) as SearchResultItem[];
		const docs = (formValue.documents ?? []) as DocumentFile[];
		const payload: CreateAdditionalAssignmentBody = {
			startOfCare: formatDateToISOString(formValue.startOfCare) ?? undefined,
			categoryTypeOfCareId: formValue.categoryTypeOfCareId!,
			ownerOfAdditionalAssignmentId:
				participants.map((p) => p.item.objectId).filter((id): id is string => !!id)[0] ?? '',
			careRelatedHandycapId: formValue.careRelatedHandycapId ?? null,
			documentIds: docs.map((d) => d.id),
		};

		this.isLoading.set(true);
		try {
			await this.additionalAssignmentApi.create(clientId, assignmentId, payload);
			await this.state.updateAssignments();
			await this.cancel();
			this.ntfy.addNotification(
				createSuccessNotification(
					this.transloco.translate('base.notification_message.additional_assignment_create_success'),
				),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(
					this.transloco.translate('base.notification_message.additional_assignment_create_error'),
				),
			);
		} finally {
			this.isLoading.set(false);
		}
	}

	public async cancel() {
		this.draftService.deleteDraft('additional', this.draftId);
		await this.router.navigate(['../'], { relativeTo: this.route });
	}

	private async loadTypesOfCare() {
		const assignmentId = this.assignment()?.id;

		const res = await this.categoriesApi.getAllTypesOfCare(
			this.env.pagination.offset,
			this.env.pagination.range,
			false,
			true,
			false,
			assignmentId,
		);
		const typesOfCare = res.typesOfCare ?? [];
		this.typesOfCare.set(typesOfCare);
		if (typesOfCare.length === 1) {
			this.form.controls.categoryTypeOfCareId.setValue(typesOfCare[0].id);
		}
	}

	private async loadSpecialistUsers(categoryTypeOfCareId: number) {
		this.usersPage.set({
			entities: [],
			offset: this.env.pagination.offset,
			range: this.env.pagination.range,
			totalEntities: 0,
		});
		try {
			const res = await this.userService.getBySpecialization(
				categoryTypeOfCareId,
				this.env.pagination.offset,
				this.env.pagination.range,
			);
			this.usersPage.set(res);
			const participantsCtrl = this.form.get('participants');
			if (res.entities?.length) {
				participantsCtrl?.enable({ emitEvent: false });
			} else {
				participantsCtrl?.setValue([]);
				participantsCtrl?.disable({ emitEvent: false });
			}
		} catch {
			this.usersPage.set({
				entities: [],
				offset: this.env.pagination.offset,
				range: this.env.pagination.range,
				totalEntities: 0,
			});
			const participantsCtrl = this.form.get('participants');
			participantsCtrl?.setValue([]);
			participantsCtrl?.disable({ emitEvent: false });
		}
	}

	private toSearchResultItem(user: User): SearchResultItem {
		return {
			item: {
				id: this.hashObjectId(user.objectId ?? user.id),
				objectId: user.objectId ?? user.id,
				name: user.lastName ?? user.displayName ?? '',
				firstName: user.firstName ?? '',
			},
			personType: PERSON_TYPE.USER,
			contactPoints: user.contactPoints ?? [],
		};
	}

	private hashObjectId(value: string | undefined): number {
		if (!value) return 0;
		let hash = 0;
		for (let i = 0; i < value.length; i += 1) {
			hash = (hash << 5) - hash + value.charCodeAt(i);
			hash |= 0;
		}
		return Math.abs(hash);
	}
}
